import React, { useState } from "react";
import { Button, Modal, ModalBody, ModalFooter, ModalHeader } from "reactstrap";
import { FiBriefcase, FiMail, FiPhone } from "react-icons/fi";
import ResumePreviewPanel from "./ResumePreviewPanel";

const sectionLabel = {
  margin: "0 0 8px",
  fontSize: 11,
  fontWeight: 800,
  textTransform: "uppercase",
  letterSpacing: "0.07em",
  color: "#94a3b8",
};

const sectionDivider = {
  border: "1px solid #e6e9f0",
  borderRadius: 12,
  padding: "14px 16px",
  marginBottom: 14,
  background: "#fbfbfe",
};

const skillColors = {
  blue: { bg: "#E6F1FB", color: "#0C447C", border: "#B5D4F4" },
  teal: { bg: "#E1F5EE", color: "#085041", border: "#9FE1CB" },
  gray: { bg: "#F1EFE8", color: "#444441", border: "#D3D1C7" },
};

const toList = value => {
  if (Array.isArray(value)) return value.filter(Boolean);
  if (typeof value === "string") {
    return value
      .split(",")
      .map(item => item.trim())
      .filter(Boolean);
  }
  return [];
};

const formatExperience = exp => {
  if (!exp) return "-";

  const [yearPart, monthPart = "0"] = String(exp).split(".");
  const years = Number(yearPart) || 0;
  const months = Number(monthPart[0]) || 0;

  return `${years} ${years === 1 ? "Year" : "Years"}${
    months > 0 ? ` ${months} ${months === 1 ? "Month" : "Months"}` : ""
  }`;
};

const getCandidateName = candidate =>
  [
    candidate?.first_name,
    candidate?.middle_name,
    candidate?.last_name,
  ]
    .filter(Boolean)
    .join(" ") || "Candidate";

const getCandidateInitials = candidate =>
  getCandidateName(candidate)
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map(name => name.charAt(0).toUpperCase())
    .join("");

const SkillPill = ({ label, color = "gray" }) => {
  const c = skillColors[color];
  return (
    <span
      style={{
        fontSize: 13,
        padding: "3px 10px",
        borderRadius: 99,
        background: c.bg,
        color: c.color,
        border: `0.5px solid ${c.border}`,
      }}
    >
      {label}
    </span>
  );
};

const renderCandidateHistoryItems = (list = [], type) => {
  if (!list || list.length === 0) {
    return <span className="text-muted" style={{ fontSize: 13 }}>-</span>;
  }

  return (
    <div className="d-flex flex-column gap-2">
      {list.map((item, i) => (
        <div
          key={`${type}-${i}`}
          style={{
            padding: "10px 12px",
            borderRadius: 10,
            border: "1px solid #e6e9f0",
            background: "#fff",
          }}
        >
          <div style={{ fontSize: 13, fontWeight: 700, color: "#0f172a" }}>
            {type === "education" && (item?.degree || item?.course || "-")}
            {type === "employment" && (item?.company_name || item?.company || "-")}
            {type === "certificates" && (item?.certificate_name || item?.name || "-")}
          </div>

          <div style={{ fontSize: 12, color: "#64748b", marginTop: 2 }}>
            {type === "education" && (
              <>
                {item?.school_name || item?.institution || "-"}
                {item?.field_of_study && ` | ${item.field_of_study}`}
                {item?.end_year && ` | ${item.end_year}`}
              </>
            )}

            {type === "employment" && (
              <>
                {item?.job_title || item?.designation || "-"}
                {(item?.start_date || item?.end_date) &&
                  ` | ${[item?.start_date, item?.end_date || "Present"].filter(Boolean).join(" - ")}`}
              </>
            )}

            {type === "certificates" && (
              <>
                {item?.issued_by || item?.issuing_organization || "-"}
                {item?.issued_date && ` | ${item.issued_date}`}
              </>
            )}
          </div>
        </div>
      ))}
    </div>
  );
};

export const normalizeStageLogs = candidate => {
  const stageLogs = Array.isArray(candidate?.stage_change_logs)
    ? candidate.stage_change_logs
    : [];

  const normalizedLogs = stageLogs.flatMap(jobEntry =>
    (Array.isArray(jobEntry?.logs) ? jobEntry.logs : []).map(log => ({
      jobTitle: jobEntry?.job_title || candidate?.job_titles || "-",
      timestamp: log?.timestamp || "-",
      createdBy: log?.created_by || "-",
      stage: log?.to_stage || log?.stage || "-",
      rejected_round:
        log?.rejected_round ||
        (String(log?.to_stage || log?.stage || "").toLowerCase() === "rejected"
          ? jobEntry?.rejected_round || candidate?.rejected_round || ""
          : ""),
      reason: log?.reason || "-",
    }))
  );

  if (normalizedLogs.length) return normalizedLogs;

  return toList(candidate?.linked_jobs).flatMap(job => {
    const logs = typeof job?.stage_change_logs === "string"
      ? job.stage_change_logs.split("\n").map(item => item.trim()).filter(Boolean)
      : [];

    return logs.map(log => ({
      jobTitle: job?.job_name || job?.job_title || candidate?.job_titles || "-",
      timestamp: job?.timestamp || "-",
      createdBy: job?.created_by_name || "-",
      stage: job?.stage || "-",
      rejected_round: job?.rejected_round || candidate?.rejected_round || "",
      reason: log,
    }));
  });
};

const ViewCandidatePopupModal = ({
  isOpen,
  toggle,
  candidate,
  onPrevious,
  onNext,
  previousDisabled = true,
  nextDisabled = true,
}) => {
  const [candidateDetailPanel, setCandidateDetailPanel] = useState("contact");

  const selectedPrimarySkills = toList(candidate?.primary_skill_names || candidate?.primary_skills);
  const selectedSecondarySkills = toList(candidate?.secondary_skill_names || candidate?.secondary_skills);
  const selectedOtherSkills = toList(candidate?.other_skill_names || candidate?.other_skills);
  const selectedEducationHistory = toList(candidate?.education || candidate?.education_history);
  const selectedEmploymentHistory = toList(candidate?.employment || candidate?.employment_history);
  const selectedCertificateHistory = toList(candidate?.certificates || candidate?.certificate_history);

  return (
    <Modal
      isOpen={isOpen}
      toggle={toggle}
      size="xl"
      centered
      className="candidate-modal"
      style={{ maxWidth: "96vw", width: "96vw" }}
      contentClassName="d-flex flex-column"
    >
      <ModalHeader
        toggle={toggle}
        style={{
          border: "none",
          paddingBottom: 12,
          background: "linear-gradient(135deg, #eef2ff, #f5f3ff)",
          borderRadius: "0.3rem 0.3rem 0 0",
        }}
      >
        <div className="d-flex align-items-center gap-3">
          <div
            style={{
              width: 48,
              height: 48,
              borderRadius: "50%",
              background: "linear-gradient(135deg, #6366f1, #7c3aed)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontWeight: 700,
              color: "#fff",
              boxShadow: "0 6px 14px rgba(99,102,241,0.35)",
            }}
          >
            {getCandidateInitials(candidate)}
          </div>

          <div>
            <h5 className="mb-0 candidate-detail-name" style={{ fontWeight: 700, color: "#0f172a" }}>
              {getCandidateName(candidate)}
            </h5>
            <small style={{ color: "#64748b" }}>
              {candidate?.current_company || "-"} &middot; {formatExperience(candidate?.total_experience)} Experience
            </small>
          </div>
        </div>
      </ModalHeader>

      <ModalBody className="p-0 d-flex flex-column" style={{ flex: "1 1 auto", minHeight: 0 }}>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "0.65fr 1.85fr",
            height: "calc(100vh - 190px)",
            minHeight: "62vh",
            maxHeight: "72vh",
          }}
        >
          <div className="ats-candidate-detail-side" style={{ borderRight: "1px solid #e6e9f0" }}>
            <div
              className="ats-candidate-detail-switch"
              style={{
                display: "flex",
                gap: 6,
                padding: 14,
                background: "#f1f5f9",
                margin: 14,
                borderRadius: 10,
              }}
            >
              <button
                type="button"
                className={candidateDetailPanel === "contact" ? "active" : ""}
                onClick={() => setCandidateDetailPanel("contact")}
                style={{
                  flex: 1,
                  border: "none",
                  borderRadius: 8,
                  padding: "8px 10px",
                  fontSize: 13,
                  fontWeight: 600,
                  cursor: "pointer",
                  background: candidateDetailPanel === "contact" ? "#fff" : "transparent",
                  color: candidateDetailPanel === "contact" ? "#4f46e5" : "#64748b",
                  boxShadow: candidateDetailPanel === "contact" ? "0 2px 10px rgba(15, 23, 42, 0.06)" : "none",
                }}
              >
                Contact & History
              </button>
              <button
                type="button"
                className={candidateDetailPanel === "skills" ? "active" : ""}
                onClick={() => setCandidateDetailPanel("skills")}
                style={{
                  flex: 1,
                  border: "none",
                  borderRadius: 8,
                  padding: "8px 10px",
                  fontSize: 13,
                  fontWeight: 600,
                  cursor: "pointer",
                  background: candidateDetailPanel === "skills" ? "#fff" : "transparent",
                  color: candidateDetailPanel === "skills" ? "#4f46e5" : "#64748b",
                  boxShadow: candidateDetailPanel === "skills" ? "0 2px 10px rgba(15, 23, 42, 0.06)" : "none",
                }}
              >
                Skills
              </button>
            </div>

            <div className="ats-candidate-detail-content" style={{ padding: "0 18px 18px" }}>
              {candidateDetailPanel === "contact" && (
                <>
                  <section style={sectionDivider}>
                    <p style={sectionLabel}>Contact</p>
                    <div className="d-flex flex-column gap-2">
                      {[
                        { Icon: FiMail, val: candidate?.email },
                        { Icon: FiPhone, val: candidate?.mobile_phone },
                        { Icon: FiBriefcase, val: `${formatExperience(candidate?.total_experience)} Total Experience` },
                      ].map(({ Icon, val }, index) => (
                        <div
                          key={index}
                          className="d-flex align-items-start gap-2"
                          style={{ fontSize: 13.5 }}
                        >
                          <Icon
                            size={16}
                            color="#4f46e5"
                            className="candidate-contact-icon mt-1"
                            style={{ minWidth: 16, color: "#4f46e5", stroke: "#4f46e5" }}
                          />
                          <span style={{ color: "#0f172a", wordBreak: "break-all" }}>{val || "-"}</span>
                        </div>
                      ))}
                    </div>
                  </section>

                  <section style={sectionDivider}>
                    <p style={sectionLabel}>Education</p>
                    {renderCandidateHistoryItems(selectedEducationHistory, "education")}
                  </section>

                  <section style={sectionDivider}>
                    <p style={sectionLabel}>Employment</p>
                    {renderCandidateHistoryItems(selectedEmploymentHistory, "employment")}
                  </section>

                  <section style={sectionDivider}>
                    <p style={sectionLabel}>Certificates</p>
                    {renderCandidateHistoryItems(selectedCertificateHistory, "certificates")}
                  </section>
                </>
              )}

              {candidateDetailPanel === "skills" && (
                <>
                  <section style={sectionDivider}>
                    <p style={sectionLabel}>Primary Skills</p>
                    <div className="d-flex flex-wrap gap-2">
                      {selectedPrimarySkills.length > 0
                        ? selectedPrimarySkills.map((skill, i) => <SkillPill key={`${skill}-${i}`} label={skill} color="blue" />)
                        : <span className="text-muted" style={{ fontSize: 13 }}>-</span>}
                    </div>
                  </section>

                  <section style={sectionDivider}>
                    <p style={sectionLabel}>Secondary Skills</p>
                    <div className="d-flex flex-wrap gap-2">
                      {selectedSecondarySkills.length > 0
                        ? selectedSecondarySkills.map((skill, i) => <SkillPill key={`${skill}-${i}`} label={skill} color="teal" />)
                        : <span className="text-muted" style={{ fontSize: 13 }}>-</span>}
                    </div>
                  </section>

                  <section style={sectionDivider}>
                    <p style={sectionLabel}>Other Skills</p>
                    <div className="d-flex flex-wrap gap-2">
                      {selectedOtherSkills.length > 0
                        ? selectedOtherSkills.map((skill, i) => <SkillPill key={`${skill}-${i}`} label={skill} color="gray" />)
                        : <span className="text-muted" style={{ fontSize: 13 }}>-</span>}
                    </div>
                  </section>
                </>
              )}

            </div>
          </div>

          <ResumePreviewPanel resumeUrl={candidate?.resume} minHeight="100%" />
        </div>
      </ModalBody>

      <ModalFooter
        className="justify-content-center"
        style={{
          border: "none",
          gap: 10,
          padding: "1rem 1.5rem 1.25rem",
          background: "#fff",
          flex: "0 0 auto",
        }}
      >
        <Button
          onClick={onPrevious}
          disabled={previousDisabled}
          style={{ background: "#fff", color: "#0f172a", border: "1px solid #e6e9f0", borderRadius: 10 }}
        >
          &laquo; Previous
        </Button>
        <Button
          onClick={toggle}
          style={{ background: "#4f46e5", border: "none", borderRadius: 10, padding: "0.45rem 1.35rem" }}
        >
          Close
        </Button>
        <Button
          onClick={onNext}
          disabled={nextDisabled}
          style={{ background: "#fff", color: "#0f172a", border: "1px solid #e6e9f0", borderRadius: 10 }}
        >
          Next &raquo;
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default ViewCandidatePopupModal;
